import { type FC, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { enableSdkOnboardingClarification, experimentationGetStarted } from '@gonfalon/dogfood-flags/dogfood-flags';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { toHref, toProjectEnvironmentKeys, toProjectEnvironments } from '@gonfalon/navigator/navigators';
import { getSdkInfoByKey } from '@gonfalon/sdk-info/sdkInfo';
import { Checkbox, Heading, Link, Text } from '@launchpad-ui/components';

import { trackProjectSettingsLinkClicked, trackSdkKeysLinkClicked, trackSdkSetupEvent } from '../../analytics';
import { useOnboardingStorage } from '../../hooks';
import { SdkKeyCallout } from '../SdkKeyCallout/SdkKeyCallout';

import { Experimentation, isExperimentationSupported } from './experimentation';
import { InstallPackages, isInstallableSdk } from './InstallPackages';
import { SdkInitInstructions } from './SdkInitInstructions';

import styles from './SdkSetupSnippets.module.css';

type ProjectSettingsLinkProps = {
  projectKey: string;
  sdk: string;
};

const ProjectSettingsLink = ({ projectKey, sdk }: ProjectSettingsLinkProps) => (
  <Link href={toHref(toProjectEnvironments({ projectKey }))} onPress={() => trackProjectSettingsLinkClicked(sdk)}>
    {`Project settings > Environments`}
  </Link>
);

type SdkKeysLinkProps = {
  projectKey: string;
  environmentKey: string;
  sdk: string;
};

const SdkKeysLink = ({ projectKey, environmentKey, sdk }: SdkKeysLinkProps) => (
  <Link
    href={toHref(toProjectEnvironmentKeys({ projectKey, environmentKey }))}
    onPress={() => trackSdkKeysLinkClicked(sdk)}
    target="_blank"
  >
    SDK keys
  </Link>
);

export const SdkSetupSnippets: FC = () => {
  const { sdk } = useOnboardingStorage();
  const {
    project: { key: projectKey },
    selectedEnvironment,
  } = useProjectContext();
  const [searchParams] = useSearchParams();
  const [showExperimentSnippet, setShowExperimentSnippet] = useState(
    () => searchParams.get('firstAction') === 'experiments',
  );

  const sdkInfo = getSdkInfoByKey(sdk);
  const hasSnippets = Boolean(sdkInfo.initSnippet);
  const hasExperimentSnippet = experimentationGetStarted() && isExperimentationSupported(sdk);

  return (
    <div className={styles.container}>
      {isInstallableSdk(sdk) && (
        <section className={styles.labeledSection}>
          <Heading level={3}>Install the package</Heading>
          <div className={styles.snippetSection}>
            <InstallPackages key={sdk} sdk={sdk} />
            {hasExperimentSnippet && (
              <Checkbox
                isSelected={showExperimentSnippet}
                onChange={(checked) => {
                  setShowExperimentSnippet(checked);
                  trackSdkSetupEvent('Show Experimentation Guidance Checked', { checked });
                }}
              >
                <span>
                  Show guidance for <b>Guarded Releases</b> and <b>Experimentation</b>
                </span>
              </Checkbox>
            )}
          </div>
        </section>
      )}
      <section className={styles.labeledSection}>
        <Heading level={3}>Initialize the SDK</Heading>
        <div className={styles.snippetSection}>
          {enableSdkOnboardingClarification() && (
            <QuietErrorBoundary severity="low">
              <SdkKeyCallout hasSnippets={hasSnippets} />
            </QuietErrorBoundary>
          )}
          <SdkInitInstructions />
        </div>
      </section>
      {hasExperimentSnippet && showExperimentSnippet && <Experimentation sdk={sdk} />}
      <Text className={styles.sdkKeysInstructions}>
        {enableSdkOnboardingClarification() ? (
          <>
            To view all your keys go to{' '}
            <SdkKeysLink projectKey={projectKey} environmentKey={selectedEnvironment.key} sdk={sdk} />.
          </>
        ) : (
          <>
            Get your SDK keys any time in <ProjectSettingsLink projectKey={projectKey} sdk={sdk} />.
          </>
        )}
      </Text>
    </div>
  );
};
